import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import type { DesktopScannerAPI, ScannerDevices, ScannerOptions, SessionResponse } from '@worki/shared';
import { scannerJobs } from '../lib/scannerJobs.js';
import { ScannerActivity } from './ScannerActivity.js';
import { Modal } from './Modal.js';
import { Button, Field, Select } from './ui.js';
interface ScannerButtonProps {
  contextKey: string; active?: boolean; disabled?: boolean;
  /** The receiver uploads to an immutable captured destination without mounted form state. */
  backgroundSafe?: boolean;
  onScanned: (file: File, signal?: AbortSignal) => void | Promise<unknown>;
}
export function ScannerButton(props: ScannerButtonProps) {
  const bridge = typeof window === 'undefined' ? undefined : window.worki?.scanner;
  return bridge ? <ConnectedScannerButton {...props} bridge={bridge} /> : null;
}
function ConnectedScannerButton({ contextKey, active = true, disabled = false, backgroundSafe = false, onScanned, bridge }: ScannerButtonProps & { bridge: DesktopScannerAPI }) {
  const { t } = useTranslation(); const location = useLocation();
  const session = useQuery<SessionResponse | null>({ queryKey: ['session'], enabled: false }).data;
  const account = session ? JSON.stringify([session.business.id, session.user.id]) : null;
  const scope = JSON.stringify([contextKey, location.pathname, location.search, account]);
  const current = useRef(scope); current.current = scope;
  const receiveNow = useRef(onScanned); receiveNow.current = onScanned;
  const alive = useRef(true);
  const request = useRef<string | null>(null);
  const [open, setOpen] = useState(false); const [listing, setListing] = useState(false);
  const [found, setFound] = useState<ScannerDevices>({ devices: [], failedDrivers: [] });
  const [editPages, setEditPages] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [source, setSource] = useState<ScannerOptions['source']>('glass');
  const [dpi, setDpi] = useState<ScannerOptions['dpi']>(300);
  const [color, setColor] = useState<ScannerOptions['color']>('color');
  const [error, setError] = useState<string | null>(null); const [remembered, setRemembered] = useState(false);
  const fieldId = useId(); const job = useSyncExternalStore(scannerJobs.subscribe, scannerJobs.getSnapshot);
  const ownJob = job?.origin.account === account && job.origin.context === contextKey ? job : null;
  const busy = listing ? 'list' : ownJob?.phase === 'scan' ? 'scan' : ownJob?.phase === 'saving' ? 'saving' : null;
  const pending = Boolean(ownJob?.file);
  const cancelDiscovery = () => { const id = request.current; request.current = null; if (id) void bridge.cancel(id).catch(() => undefined); };
  const close = () => { cancelDiscovery(); setOpen(false); setListing(false); setError(null); if (ownJob) scannerJobs.hide(); };
  useLayoutEffect(() => { cancelDiscovery(); setOpen(false); setListing(false); setError(null); }, [scope]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; cancelDiscovery(); }; }, []);
  useEffect(() => { scannerJobs.setAccount(account); }, [account]);
  useEffect(() => { if (!active && ownJob) scannerJobs.hide(); }, [active, ownJob?.id]);
  useEffect(() => {
    const expected = scope;
    return () => queueMicrotask(() => {
      if (scannerJobs.getSnapshot()?.origin.context === contextKey && (!alive.current || current.current !== expected)) scannerJobs.hide();
    });
  }, [scope]);
  useEffect(() => { if (ownJob?.phase === 'done') setOpen(false); }, [ownJob?.phase]);
  const discover = async (refresh = false) => {
    const id = crypto.randomUUID(); request.current = id; const expected = scope;
    const owns = () => request.current === id && current.current === expected && alive.current;
    setOpen(true); setListing(true); setError(null);
    try {
      if (!refresh && bridge.getPreferences) {
        const saved = await bridge.getPreferences();
        if (!owns()) return;
        if (saved.ok && saved.value) {
          const { device, options } = saved.value;
          setFound({ devices: [device], failedDrivers: [] }); setDeviceId(device.id);
          setSource(options.source); setDpi(options.dpi); setColor(options.color); setRemembered(true); return;
        }
      }
      const result = await bridge.listDevices(id, refresh);
      if (!owns()) return;
      if (!result.ok) { setError(`scanner.errors.${result.code}`); return; }
      setFound(result.value); setDeviceId(result.value.devices.some(d => d.id === deviceId) ? deviceId : result.value.devices[0]?.id ?? ''); setRemembered(false);
    } catch { if (owns()) setError('scanner.errors.discoveryFailed'); }
    finally { if (owns()) { request.current = null; setListing(false); } }
  };
  const show = () => { if (ownJob && ownJob.phase !== 'done') { scannerJobs.show(); setOpen(true); } else void discover(); };
  const scan = async () => {
    if (!account) return;
    if (pending) { await scannerJobs.retry(onScanned); return; }
    if (ownJob?.phase === 'error') await scannerJobs.cancel();
    const expected = scope; const receive = onScanned;
    const kind = contextKey.startsWith('library:') ? 'library' : contextKey.startsWith('invoices:') ? 'invoices' : contextKey.startsWith('signing:') ? 'signing' : 'other';
    try {
      scannerJobs.setAccount(account);
      scannerJobs.start(bridge, { deviceId, source, dpi, color }, { account, context: contextKey, kind,
        folderId: kind === 'invoices' ? contextKey.slice('invoices:'.length) : undefined, href: location.pathname + location.search },
        () => backgroundSafe ? receive : alive.current && current.current === expected ? receiveNow.current : null, editPages);
      setError(null);
    } catch { setError('scanner.errors.busy'); }
  };
  const locked = Boolean(job && job.phase !== 'done' && !ownJob);
  return <>
    <Button type="button" size="sm" disabled={disabled || !active || locked || !account} icon={<ScanLine className="h-4 w-4" aria-hidden />} onClick={show}>{t('scanner.title')}</Button>
    <Modal open={(open || Boolean(ownJob?.visible)) && active && ownJob?.phase !== 'review'} title={t('scanner.title')} onClose={close} layer={80} footer={<>
      <Button type="button" variant="ghost" disabled={busy === 'saving'} onClick={() => { void scannerJobs.cancel(); close(); }}>{t('common.cancel')}</Button>
      {busy === 'scan' || busy === 'saving' ? <Button type="button" onClick={close}>{t('scanner.background')}</Button> :
        <Button type="button" variant="primary" disabled={listing || (!deviceId && !pending)} onClick={() => void scan()}>{t(pending ? 'scanner.addResult' : 'scanner.scan')}</Button>}
    </>}>
      {busy && <ScannerActivity phase={busy} />}
      {(error || ownJob?.error) && <p role="alert" className="text-sm text-destructive">{t(error ?? ownJob!.error!)}</p>}
      {remembered && <p className="text-sm text-muted-foreground">{t('scanner.remembered')}</p>}
      {error === 'scanner.errors.notInstalled' && <p className="text-sm">{t('scanner.repairHint')}</p>}
      {!busy && !error && found.devices.length === 0 && !pending && <p role="status" className="text-sm">{t('scanner.noDevices')}</p>}
      {found.failedDrivers.length > 0 && <p className="text-sm text-muted-foreground">{t('scanner.partialDiscovery')}</p>}
      <Field label={t('scanner.device')} htmlFor={`${fieldId}-device`}><Select id={`${fieldId}-device`} value={deviceId} disabled={busy !== null || pending} onChange={setDeviceId}>
        {found.devices.length === 0 && <option value="">—</option>}{found.devices.map(device => <option key={device.id} value={device.id}>{device.name} ({device.driver.toUpperCase()})</option>)}
      </Select></Field>
      <Field label={t('scanner.source')} htmlFor={`${fieldId}-source`}><Select id={`${fieldId}-source`} value={source} disabled={busy !== null || pending} onChange={value => setSource(value as ScannerOptions['source'])}>
        {(['glass', 'feeder', 'duplex'] as const).map(value => <option key={value} value={value}>{t(`scanner.${value}`)}</option>)}
      </Select></Field>
      <Field label={t('scanner.resolution')} htmlFor={`${fieldId}-dpi`}><Select id={`${fieldId}-dpi`} value={String(dpi)} disabled={busy !== null || pending} onChange={value => setDpi(Number(value) as ScannerOptions['dpi'])}>
        {[150, 300, 600].map(value => <option key={value} value={value}>{value} DPI</option>)}
      </Select></Field>
      <Field label={t('scanner.color')} htmlFor={`${fieldId}-color`}><Select id={`${fieldId}-color`} value={color} disabled={busy !== null || pending} onChange={value => setColor(value as ScannerOptions['color'])}>
        {(['color', 'gray', 'bw'] as const).map(value => <option key={value} value={value}>{t(value === 'color' ? 'scanner.fullColor' : `scanner.${value}`)}</option>)}
      </Select></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editPages} disabled={busy !== null || pending} onChange={event => setEditPages(event.target.checked)} />{t('scanner.editAfterScan')}</label>
      <p className="text-xs text-muted-foreground">{t('scanner.settingsHint')}</p>
      <Button type="button" size="sm" disabled={busy !== null || pending} onClick={() => void discover(true)}>{t('scanner.refresh')}</Button>
    </Modal>
  </>;
}
